import { supabase } from '../lib/supabase';

export const communicationService = {
  // ============================================================================
  // TEMPLATE OPERATIONS
  // ============================================================================

  async getAllTemplates(filters = {}) {
    const page = filters.page || 1;
    const pageSize = filters.pageSize || 50;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('provider_message_templates')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(from, to);

    if (filters.messageType) {
      query = query.eq('message_type', filters.messageType);
    }
    if (filters.templateType) {
      query = query.eq('template_type', filters.templateType);
    }
    if (filters.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive);
    }
    if (filters.isStatic !== undefined) {
      query = query.eq('is_static_message', filters.isStatic);
    }
    if (filters.isRuntime !== undefined) {
      query = query.eq('is_runtime_message', filters.isRuntime);
    }
    // Filter by trigger_applicable_type (for MANUAL sends, show MANUAL and BOTH)
    if (filters.triggerType === 'MANUAL') {
      // Show templates that can be used manually (MANUAL or BOTH)
      query = query.in('trigger_applicable_type', ['MANUAL', 'BOTH']);
    } else if (filters.triggerType === 'AUTOMATIC') {
      // Show templates that can be used automatically (AUTOMATIC or BOTH)
      query = query.in('trigger_applicable_type', ['AUTOMATIC', 'BOTH']);
    }
    // Add search filter (only on text fields - enums cannot use ILIKE)
    if (filters.search && filters.search.trim().length > 0) {
      const searchTerm = filters.search.trim();
      // Search only on text fields: name and template_message
      // For enum fields (template_type, message_type), users should use the filter dropdowns
      query = query.or(`name.ilike.%${searchTerm}%,template_message.ilike.%${searchTerm}%`);
    }

    const { data, error, count } = await query;
    if (error) throw error;
    return { data: data || [], total: count || 0 };
  },

  async getTemplateById(templateId) {
    const { data, error } = await supabase
      .from('provider_message_templates')
      .select('*')
      .eq('id', templateId)
      .single();

    if (error) throw error;
    return data;
  },

  async createTemplate(templateData) {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('provider_message_templates')
      .insert({
        template_id: templateData.templateId,
        name: templateData.name,
        templated_vars: templateData.templatedVars || [],
        template_message: templateData.isRuntimeMessage ? null : templateData.templateMessage,
        is_static_message: templateData.isStaticMessage || false,
        is_runtime_message: templateData.isRuntimeMessage || false,
        message_type: templateData.messageType,
        trigger_applicable_type: templateData.triggerApplicableType,
        template_type: templateData.templateType,
        provider: templateData.provider,
        language: templateData.language || 'en',
        category: templateData.category,
        is_active: templateData.isActive !== false,
        approval_status: templateData.approvalStatus || 'PENDING',
        created_by: user?.id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateTemplate(templateId, updates) {
    const { data, error } = await supabase
      .from('provider_message_templates')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', templateId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteTemplate(templateId) {
    const { error } = await supabase
      .from('provider_message_templates')
      .delete()
      .eq('id', templateId);

    if (error) throw error;
  },

  async renderTemplate(template, variables = {}, runtimeMessage = null) {
    // Runtime message: use user-provided content
    if (template.is_runtime_message) {
      return runtimeMessage || '';
    }

    // Pre-defined static: return as-is
    if (template.is_static_message) {
      return template.template_message || '';
    }

    // Pre-defined templated: render with variables
    let message = template.template_message || '';
    const vars = template.templated_vars || [];
    
    vars.forEach(varName => {
      const value = variables[varName] || '';
      const regex = new RegExp(`\\{\\{${varName}\\}\\}`, 'g');
      message = message.replace(regex, value);
    });

    return message;
  },

  // ============================================================================
  // CAMPAIGN OPERATIONS
  // ============================================================================

  async getAllCampaigns(filters = {}) {
    const page = filters.page || 1;
    const pageSize = filters.pageSize || 50;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('communication_campaigns')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(from, to);

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error, count } = await query;
    if (error) throw error;
    return { data: data || [], total: count || 0 };
  },

  async getCampaignById(campaignId) {
    const { data, error } = await supabase
      .from('communication_campaigns')
      .select('*')
      .eq('id', campaignId)
      .single();

    if (error) throw error;
    return data;
  },

  async createCampaign(campaignData) {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('communication_campaigns')
      .insert({
        name: campaignData.name,
        targeted_provider_message_template_ids: campaignData.templateIds,
        communication_campaign_type: campaignData.campaignType,
        communication_campaign_desc: campaignData.description,
        recipient_criteria: campaignData.recipientCriteria,
        scheduled_at: campaignData.scheduledAt,
        runtime_message: campaignData.runtimeMessage || null,
        status: campaignData.scheduledAt ? 'SCHEDULED' : 'DRAFT',
        created_by: user?.id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateCampaign(campaignId, updates) {
    const { data, error } = await supabase
      .from('communication_campaigns')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', campaignId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteCampaign(campaignId) {
    const { error } = await supabase
      .from('communication_campaigns')
      .delete()
      .eq('id', campaignId);

    if (error) throw error;
  },

  async executeCampaign(campaignId) {
    const { data: { user } } = await supabase.auth.getUser();
    
    try {
      // Get campaign
      const campaign = await this.getCampaignById(campaignId);
      
      // Get templates
      const { data: templates, error: templatesError } = await supabase
        .from('provider_message_templates')
        .select('*')
        .in('id', campaign.targeted_provider_message_template_ids)
        .eq('is_active', true);

      if (templatesError) throw templatesError;
      if (!templates || templates.length === 0) {
        throw new Error('No active templates found for this campaign');
      }

      // Get recipients based on criteria
      const recipients = await this.getRecipientsByCriteria(campaign.recipient_criteria);

      // Update campaign status
      await this.updateCampaign(campaignId, {
        status: 'RUNNING',
        total_recipients: recipients.length,
        executed_at: new Date().toISOString(),
        executed_by: user?.id
      });

      // Execute for each recipient
      let sentCount = 0;
      let failedCount = 0;

      for (const recipient of recipients) {
        for (const template of templates) {
          let execution = null;
          try {
            // For runtime templates, use campaign.runtime_message; otherwise render with variables
            const message = template.is_runtime_message 
              ? await this.renderTemplate(template, recipient.variables || {}, campaign.runtime_message)
              : await this.renderTemplate(template, recipient.variables || {});
            
            // Validate message is not empty
            if (!message || !message.trim()) {
              throw new Error(`Message content is empty for template: ${template.name}`);
            }
            
            execution = await this.createExecution({
              campaignId,
              templateId: template.id,
              registrationId: recipient.registrationId,
              recipientMobile: recipient.mobile,
              recipientEmail: recipient.email,
              messageContent: message,
              messageType: template.message_type
            });

            // Send via provider (TODO: Implement actual provider integration)
            const result = await this.sendMessageViaProvider(template, message, recipient);
            
            // Update execution status
            await this.updateExecution(execution.id, {
              status: 'SENT',
              sent_at: new Date().toISOString(),
              provider_message_id: result.providerMessageId
            });

            sentCount++;
          } catch (error) {
            console.error(`Failed to send to ${recipient.mobile}:`, error);
            if (execution) {
              await this.updateExecution(execution.id, {
                status: 'FAILED',
                error_message: error.message
              });
            }
            failedCount++;
          }
        }
      }

      // Update campaign statistics
      await this.updateCampaign(campaignId, {
        status: 'COMPLETED',
        sent_count: sentCount,
        failed_count: failedCount
      });

      return { success: true, sent: sentCount, failed: failedCount };
    } catch (error) {
      await this.updateCampaign(campaignId, {
        status: 'FAILED'
      });
      throw error;
    }
  },

  async getRecipientsByCriteria(criteria) {
    if (!criteria) return [];

    const recipientTypes = Array.isArray(criteria.recipientType) 
      ? criteria.recipientType 
      : (criteria.recipientType ? [criteria.recipientType] : ['POC']); // Default to POC for backward compatibility
    
    const allRecipients = [];

    // Build base registration query with common filters
    let regQuery = supabase
      .from('registrations')
      .select(`
        id,
        mobile_number,
        poc_email,
        poc_name,
        status,
        city,
        age_category,
        school_id,
        schools(name)
      `);

    if (criteria.status) {
      if (Array.isArray(criteria.status)) {
        regQuery = regQuery.in('status', criteria.status);
      } else {
        regQuery = regQuery.eq('status', criteria.status);
      }
    }

    if (criteria.city) {
      regQuery = regQuery.eq('city', criteria.city);
    }

    if (criteria.ageCategory) {
      if (Array.isArray(criteria.ageCategory)) {
        regQuery = regQuery.in('age_category', criteria.ageCategory);
      } else {
        regQuery = regQuery.eq('age_category', criteria.ageCategory);
      }
    }

    if (criteria.schoolIds && Array.isArray(criteria.schoolIds)) {
      regQuery = regQuery.in('school_id', criteria.schoolIds);
    }

    const { data: registrations, error: regError } = await regQuery;
    if (regError) throw regError;

    if (!registrations || registrations.length === 0) {
      return [];
    }

    const registrationIds = registrations.map(r => r.id);

    // Fetch POCs (Point of Contact - the submitter)
    if (recipientTypes.includes('POC')) {
      registrations.forEach(reg => {
        if (reg.mobile_number || reg.poc_email) {
          allRecipients.push({
            registrationId: reg.id,
            recipientType: 'POC',
            mobile: reg.mobile_number,
            email: reg.poc_email,
            name: reg.poc_name || 'POC',
            variables: {
              name: reg.poc_name || 'POC',
              school: reg.schools?.name || 'School',
              city: reg.city,
              ageCategory: reg.age_category
            }
          });
        }
      });
    }

    // Fetch Coaches
    if (recipientTypes.includes('COACH') && registrationIds.length > 0) {
      const { data: coaches, error: coachesError } = await supabase
        .from('coaches')
        .select(`
          id,
          name,
          email,
          phone,
          registration_id,
          registrations!inner(
            id,
            mobile_number,
            poc_email,
            city,
            age_category,
            school_id,
            schools(name)
          )
        `)
        .in('registration_id', registrationIds);

      if (coachesError) throw coachesError;

      (coaches || []).forEach(coach => {
        const reg = coach.registrations;
        if (coach.phone || coach.email || reg?.mobile_number || reg?.poc_email) {
          allRecipients.push({
            registrationId: coach.registration_id,
            recipientType: 'COACH',
            mobile: coach.phone || reg?.mobile_number,
            email: coach.email || reg?.poc_email,
            name: coach.name || 'Coach',
            variables: {
              name: coach.name || 'Coach',
              school: reg?.schools?.name || 'School',
              city: reg?.city,
              ageCategory: reg?.age_category
            }
          });
        }
      });
    }

    // Fetch Players
    if (recipientTypes.includes('PLAYER') && registrationIds.length > 0) {
      const { data: players, error: playersError } = await supabase
        .from('players')
        .select(`
          id,
          name,
          email,
          phone,
          registration_id,
          registrations!inner(
            id,
            city,
            age_category,
            school_id,
            schools(name)
          )
        `)
        .in('registration_id', registrationIds);

      if (playersError) throw playersError;

      (players || []).forEach(player => {
        const reg = player.registrations;
        if (player.phone || player.email || reg?.mobile_number || reg?.poc_email) {
          allRecipients.push({
            registrationId: player.registration_id,
            recipientType: 'PLAYER',
            mobile: player.phone || reg?.mobile_number,
            email: player.email || reg?.poc_email,
            name: player.name || 'Player',
            variables: {
              name: player.name || 'Player',
              school: reg?.schools?.name || 'School',
              city: reg?.city,
              ageCategory: reg?.age_category
            }
          });
        }
      });
    }

    return allRecipients;
  },

  // ============================================================================
  // EXECUTION OPERATIONS
  // ============================================================================

  async createExecution(executionData) {
    const { data, error } = await supabase
      .from('campaign_executions')
      .insert({
        campaign_id: executionData.campaignId,
        template_id: executionData.templateId,
        registration_id: executionData.registrationId,
        recipient_mobile: executionData.recipientMobile,
        recipient_email: executionData.recipientEmail,
        message_content: executionData.messageContent,
        message_type: executionData.messageType,
        status: 'PENDING'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateExecution(executionId, updates) {
    const { data, error } = await supabase
      .from('campaign_executions')
      .update(updates)
      .eq('id', executionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getExecutionsByCampaign(campaignId, filters = {}) {
    const page = filters.page || 1;
    const pageSize = filters.pageSize || 50;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('campaign_executions')
      .select('*', { count: 'exact' })
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error, count } = await query;
    if (error) throw error;
    return { data: data || [], total: count || 0 };
  },

  // ============================================================================
  // PROVIDER INTEGRATION (Placeholder - Implement with actual providers)
  // ============================================================================

  async sendMessageViaProvider(template, message, recipient) {
    // TODO: Implement actual provider integration
    // - Twilio for SMS
    // - WhatsApp Business API for WhatsApp
    // - SendGrid/SES for Email
    
    // Simulate API call (placeholder until provider integration is implemented)
    return {
      providerMessageId: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      status: 'sent'
    };
  },

  // ============================================================================
  // QUICK SEND (Manual message without campaign)
  // ============================================================================

  async sendQuickMessage(templateId, recipients, variables = {}) {
    const template = await this.getTemplateById(templateId);
    const message = await this.renderTemplate(template, variables);

    const results = [];
    for (const recipient of recipients) {
      try {
        const result = await this.sendMessageViaProvider(template, message, recipient);
        results.push({ recipient, status: 'sent', providerMessageId: result.providerMessageId });
      } catch (error) {
        results.push({ recipient, status: 'failed', error: error.message });
      }
    }

    return results;
  }
};

